import { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Calendar, Phone, ArrowRight, Sparkles, Music } from 'lucide-react';

// Floating music note
const FloatingNote = ({ delay = 0, x = 0 }) => (
    <motion.div
        className="absolute text-3xl text-primary/10"
        style={{ left: `${x}%` }}
        animate={{
            y: [0, -100, -200],
            opacity: [0, 0.4, 0],
            rotate: [0, 20, -20],
        }}
        transition={{
            duration: 4,
            repeat: Infinity,
            delay: delay,
            ease: 'easeOut',
        }}
    >
        ♪
    </motion.div>
);

export const CTASection = () => {
    const containerRef = useRef(null);
    const isInView = useInView(containerRef, { once: true, margin: "-100px" });
    
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ['start end', 'end start'],
    });

    const scale = useTransform(scrollYProgress, [0, 0.5], [0.95, 1]);
    const opacity = useTransform(scrollYProgress, [0, 0.3], [0.5, 1]);

    return (
        <section ref={containerRef} className="py-24 lg:py-32 relative overflow-hidden">
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-background via-card/50 to-background" />
            
            {/* Floating Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[200px]"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.1, 0.2, 0.1],
                    }}
                    transition={{ duration: 8, repeat: Infinity }}
                />
                <motion.div
                    className="absolute top-1/4 right-1/4 w-64 h-64 bg-accent/15 rounded-full blur-[100px]"
                    animate={{
                        scale: [1, 1.3, 1],
                        x: [0, 30, 0],
                    }}
                    transition={{ duration: 6, repeat: Infinity, delay: 1 }}
                />
                <motion.div
                    className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-amber/10 rounded-full blur-[80px]"
                    animate={{
                        scale: [1, 1.2, 1],
                        y: [0, -20, 0],
                    }}
                    transition={{ duration: 5, repeat: Infinity, delay: 2 }}
                />

                {/* Floating Music Notes */}
                {[10, 25, 40, 55, 70, 85].map((x, i) => (
                    <FloatingNote key={i} x={x} delay={i * 0.7} />
                ))}
            </div>

            <motion.div 
                className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
                style={{ scale, opacity }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                    className="max-w-4xl mx-auto text-center"
                >
                    {/* Badge */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={isInView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ delay: 0.2 }}
                        className="inline-flex items-center gap-2 mb-8 px-5 py-2.5 rounded-full glass border border-primary/30 backdrop-blur-md"
                    >
                        <motion.div
                            animate={{ rotate: [0, 360] }}
                            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                        >
                            <Sparkles className="w-4 h-4 text-primary" />
                        </motion.div>
                        <span className="text-sm font-body text-foreground">Limited Availability</span>
                    </motion.div>

                    {/* Heading */}
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 leading-tight">
                        Ready to Create
                        <br />
                        <motion.span 
                            className="text-gradient-primary inline-block"
                            animate={{
                                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                            }}
                            transition={{ duration: 5, repeat: Infinity }}
                            style={{
                                backgroundSize: '200% 200%',
                            }}
                        >
                            Unforgettable Memories?
                        </motion.span>
                    </h2>

                    {/* Description */}
                    <p className="text-muted-foreground text-base sm:text-lg lg:text-xl font-body max-w-2xl mx-auto mb-12">
                        Book Hawayein The Band for your next event and let us transform your celebration 
                        into an extraordinary musical experience that you and your guests will cherish forever.
                    </p>

                    {/* CTA Buttons */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.4 }}
                        className="flex flex-col sm:flex-row gap-5 justify-center items-center"
                    >
                        <Link to="/book">
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Button
                                    size="lg"
                                    className="relative overflow-hidden bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_100%] text-foreground font-heading font-bold px-12 py-8 rounded-2xl text-lg hover:bg-[position:100%_0] transition-all duration-500 group shadow-glow-lg"
                                >
                                    <motion.span
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                                        animate={{ x: ['-100%', '100%'] }}
                                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                                    />
                                    <Calendar className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                    Book Your Event Now
                                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                                </Button>
                            </motion.div>
                        </Link>
                        
                        <a href="tel:+917000312302">
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="border-2 border-border/50 bg-card/30 backdrop-blur-md text-foreground font-heading font-semibold px-12 py-8 rounded-2xl text-lg hover:bg-card/50 hover:border-primary/50 group"
                                >
                                    <Phone className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                    Call: +91 7000312302
                                </Button>
                            </motion.div>
                        </a>
                    </motion.div>

                    {/* Trust Elements */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={isInView ? { opacity: 1 } : {}}
                        transition={{ delay: 0.6 }}
                        className="mt-14 flex flex-wrap justify-center items-center gap-8 text-sm text-muted-foreground font-body"
                    >
                        {[
                            { color: 'bg-success', text: 'Quick Response' },
                            { color: 'bg-primary', text: 'Flexible Packages' },
                            { color: 'bg-accent', text: 'Custom Song Requests' },
                            { color: 'bg-amber', text: 'Pan-India Service' },
                        ].map((item, i) => (
                            <motion.div
                                key={item.text}
                                className="flex items-center gap-2"
                                initial={{ opacity: 0, x: -10 }}
                                animate={isInView ? { opacity: 1, x: 0 } : {}}
                                transition={{ delay: 0.7 + i * 0.1 }}
                            >
                                <motion.div 
                                    className={`w-2.5 h-2.5 rounded-full ${item.color}`}
                                    animate={{ scale: [1, 1.3, 1] }}
                                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                                />
                                <span>{item.text}</span>
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>
            </motion.div>
        </section>
    );
};
